import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';

export interface FileFormData {
  name: string;
  type: string;
  size: number;
  preview: string;
  binary: Uint8Array;
}

interface LogoUploaderProps {
  type: 'logo' | 'favicon';
  zoom: number;
  defaultImage?: string | null;
  onFileSelect: (file: FileFormData | null) => void;
  onAlert: (message: string) => void;
  resetKey?: number;
}

const LOGO_MAX_W = 200;
const LOGO_MAX_H = 60;
const FAVICON_MAX_W = 32;
const FAVICON_MAX_H = 32;
const MAX_SIZE = 500 * 1024; // 500 KB

const validateDimensions = (file: File, type: 'logo' | 'favicon'): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ok =
        type === 'logo'
          ? img.width <= LOGO_MAX_W && img.height <= LOGO_MAX_H
          : img.width <= FAVICON_MAX_W && img.height <= FAVICON_MAX_H;
      URL.revokeObjectURL(url);
      resolve(ok);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
    img.src = url;
  });

const LogoUploader: React.FC<LogoUploaderProps> = ({
  type,
  zoom,
  defaultImage,
  onFileSelect,
  onAlert,
  resetKey,
}) => {
  const [selectedFile, setSelectedFile] = useState<FileFormData | null>(null);

  useEffect(() => {
    if (resetKey !== undefined) {
      if (selectedFile?.preview) {
        URL.revokeObjectURL(selectedFile.preview);
      }
      setSelectedFile(null);
    }
  }, [resetKey, selectedFile?.preview]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;
      const file = acceptedFiles[0];

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        onAlert('Unsupported file type. Please use JPEG, JPG, PNG, or GIF.');
        return;
      }

      if (file.size > MAX_SIZE) {
        onAlert('File size exceeds the maximum limit of 500 KB.');
        return;
      }

      const dimensionsOk = await validateDimensions(file, type);
      if (!dimensionsOk) {
        const limit = type === 'logo' ? '200×60 px' : '32×32 px';
        onAlert(`Image dimensions exceed the maximum limit of ${limit}.`);
        return;
      }

      const arrayBuffer = await file.arrayBuffer();
      const fileData: FileFormData = {
        name: file.name,
        type: file.type,
        size: file.size,
        preview: URL.createObjectURL(file),
        binary: new Uint8Array(arrayBuffer),
      };
      setSelectedFile(fileData);
      onFileSelect(fileData);
    },
    [type, onFileSelect, onAlert]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: false,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
  });

  const previewSrc = selectedFile?.preview || defaultImage;
  const isLogo = type === 'logo';
  const scale = zoom / 100;

  return (
    <div className="wl-uploader-wrapper">
      <div className={`wl-upload-box ${isLogo ? 'wl-upload-box-logo' : 'wl-upload-box-favicon'}`} {...getRootProps()}>
        <input {...getInputProps()} />
        {previewSrc ? (
          <div className="wl-preview-wrap">
            <img
              src={previewSrc}
              alt={isLogo ? 'Logo preview' : 'Favicon preview'}
              className="wl-preview-img"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'center',
                transition: 'transform 0.3s ease',
                maxWidth: isLogo ? '150px' : '28px',
                maxHeight: isLogo ? '50px' : '28px',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : (
          <div className="wl-upload-prompt" onClick={open}>
            <Upload size={isLogo ? 20 : 16} className="wl-upload-icon" />
            <span className="wl-upload-label">
              {isLogo ? 'Upload Logo' : 'Upload Favicon'}
            </span>
            <span className="wl-upload-hint">Drop file or click</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoUploader;
